import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import './DataImport.css';

interface RideInfo {
  recorded_at: string | null;
  start_date_local: string | null;
  duration_seconds: number | null;
  data_points: number | null;
  has_power: boolean;
  has_hr: boolean;
  np_watts: number | null;
  tss: number | null;
  activity_name: string | null;
  activity_type: string | null;
}

interface WhoopData {
  date: string;
  recovery_score: number | null;
  resting_heart_rate: number | null;
  hrv_rmssd_milli: number | null;
  spo2_percentage: number | null;
  skin_temp_celsius: number | null;
  sleep_score: number | null;
  sleep_efficiency: number | null;
  sleep_consistency: number | null;
  total_in_bed_ms: number | null;
  total_light_sleep_ms: number | null;
  total_sws_ms: number | null;
  total_rem_ms: number | null;
  total_awake_ms: number | null;
  sleep_cycle_count: number | null;
  disturbance_count: number | null;
  respiratory_rate: number | null;
  sleep_needed_ms: number | null;
  strain_score: number | null;
  average_heart_rate: number | null;
  max_heart_rate: number | null;
  kilojoules: number | null;
  workouts_json: any[] | null;
}

interface HealthData {
  resting_heart_rate: number | null;
  hrv_sdnn_ms: number | null;
  spo2_percentage: number | null;
  respiratory_rate: number | null;
  sleep_hours: number | null;
  in_bed_hours: number | null;
  deep_sleep_minutes: number | null;
  rem_sleep_minutes: number | null;
  core_sleep_minutes: number | null;
  awake_minutes: number | null;
}

interface ImportRecord {
  id: number | string;
  filename: string;
  status: string;
  source_type: string;
  job_id: string | null;
  error_message: string | null;
  matched_activity_id: number | null;
  activity_stream_id: number | null;
  created_at: string;
  completed_at: string | null;
  ride_info: RideInfo | null;
  deletable: boolean;
  whoop_data?: WhoopData | null;
  health_data?: HealthData | null;
}

interface Pagination {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

interface FitUploadResult {
  success: boolean;
  import_id: number;
  stream_id: number;
  matched_activity_id: number | null;
  summary: {
    data_points: number;
    has_power: boolean;
    has_hr: boolean;
    duration_seconds: number;
  };
}

interface BulkUploadResult {
  success: boolean;
  job_id: string;
  total_files: number;
  processed: number;
  failed: number;
  pending: number;
  background_processing: boolean;
  results: Array<{
    filename: string;
    status: string;
    import_id: number | null;
    matched_activity_id: number | null;
  }>;
}

interface CsvUploadResult {
  success: boolean;
  job_id: string;
  total_rows: number;
  processed: number;
  matched: number;
  results: Array<{
    date: string;
    title: string;
    status: string;
  }>;
}

interface ProcessingStatus {
  job_id: string;
  total: number;
  completed: number;
  failed: number;
  pending: number;
  processing: number;
  is_done: boolean;
  results: Array<{
    filename: string;
    status: string;
    error_message: string | null;
    matched_activity_id: number | null;
  }>;
}

type UploadState = 'idle' | 'dragging' | 'uploading' | 'success' | 'error';
type UploadResultType =
  | { type: 'fit'; data: FitUploadResult }
  | { type: 'bulk'; data: BulkUploadResult }
  | { type: 'csv'; data: CsvUploadResult };
type SourceFilter = 'all' | 'import' | 'strava' | 'whoop' | 'health';

type ColumnId = 'name' | 'filename' | 'source' | 'status' | 'date' | 'duration' | 'np' | 'tss' | 'type' | 'activity' | 'imported' | 'data_points' | 'hr' | 'error';

interface ColumnDef {
  id: ColumnId;
  label: string;
  defaultVisible: boolean;
  width: string;
}

const ALL_COLUMNS: ColumnDef[] = [
  { id: 'name', label: 'Name', defaultVisible: true, width: '250px' },
  { id: 'date', label: 'Activity Date', defaultVisible: true, width: '140px' },
  { id: 'duration', label: 'Duration', defaultVisible: true, width: '80px' },
  { id: 'np', label: 'NP', defaultVisible: true, width: '70px' },
  { id: 'tss', label: 'TSS', defaultVisible: true, width: '60px' },
  { id: 'type', label: 'Type', defaultVisible: true, width: '100px' },
  { id: 'filename', label: 'File', defaultVisible: false, width: '140px' },
  { id: 'source', label: 'Source', defaultVisible: false, width: '70px' },
  { id: 'status', label: 'Status', defaultVisible: false, width: '90px' },
  { id: 'activity', label: 'Activity', defaultVisible: false, width: '140px' },
  { id: 'imported', label: 'Imported', defaultVisible: false, width: '100px' },
  { id: 'data_points', label: 'Points', defaultVisible: false, width: '60px' },
  { id: 'hr', label: 'HR', defaultVisible: false, width: '40px' },
  { id: 'error', label: 'Error', defaultVisible: false, width: '120px' },
];

const MAX_FIT_SIZE = 50 * 1024 * 1024;
const MAX_ZIP_SIZE = 250 * 1024 * 1024;
const MAX_CSV_SIZE = 50 * 1024 * 1024;
const GCS_UPLOAD_THRESHOLD = 30 * 1024 * 1024; // Use signed URL for ZIPs > 30MB
const POLL_INTERVAL = 3000;
const POLL_TIMEOUT = 30 * 60 * 1000;
const HISTORY_REFRESH_INTERVAL = 5000;
const PER_PAGE_OPTIONS = [20, 50, 100];

const DataImport: React.FC = () => {
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [uploadResult, setUploadResult] = useState<UploadResultType | null>(null);
  const [uploadError, setUploadError] = useState<string>('');
  const [uploadingFilename, setUploadingFilename] = useState<string>('');
  const [jobStatuses, setJobStatuses] = useState<Map<string, ProcessingStatus>>(new Map());

  // Import Manager state
  const [imports, setImports] = useState<ImportRecord[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, per_page: 20, total: 0, total_pages: 1 });
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [historyLoading, setHistoryLoading] = useState(true);
  const [hasActiveJobs, setHasActiveJobs] = useState(false);
  const [activeJobIds, setActiveJobIds] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number | string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<number | string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [detailRecord, setDetailRecord] = useState<ImportRecord | null>(null);

  // Column visibility & order - persisted in localStorage
  const [visibleColumns, setVisibleColumns] = useState<ColumnId[]>(() => {
    const saved = localStorage.getItem('di-columns');
    if (saved) {
      try { return JSON.parse(saved); } catch { }
    }
    return ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.id);
  });

  // Column config dropdown
  const [showColumnMenu, setShowColumnMenu] = useState(false);

  // Column widths - resizable by user
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const saved = localStorage.getItem('di-column-widths');
    if (saved) {
      try { return JSON.parse(saved); } catch { }
    }
    return {};
  });
  const resizingRef = useRef<{ colId: string; startX: number; startWidth: number } | null>(null);

  const getColumnWidth = (colId: ColumnId): number => {
    if (columnWidths[colId]) return columnWidths[colId];
    const col = ALL_COLUMNS.find(c => c.id === colId);
    return col ? parseInt(col.width) : 100;
  };

  const handleResizeStart = (e: React.MouseEvent, colId: ColumnId) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = getColumnWidth(colId);
    resizingRef.current = { colId, startX, startWidth };

    const onMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const diff = ev.clientX - resizingRef.current.startX;
      const newWidth = Math.max(50, resizingRef.current.startWidth + diff);
      setColumnWidths(prev => ({ ...prev, [resizingRef.current!.colId]: newWidth }));
    };
    const onMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const pollingStartRef = useRef<number>(0);
  const historyRefreshRef = useRef<NodeJS.Timeout | null>(null);
  const paginationRef = useRef(pagination);
  const sourceFilterRef = useRef(sourceFilter);
  const startPollingRef = useRef<(jobId: string) => void>(() => {});
  const columnMenuRef = useRef<HTMLDivElement>(null);
  paginationRef.current = pagination;
  sourceFilterRef.current = sourceFilter;

  // Persist visible columns & widths
  useEffect(() => {
    localStorage.setItem('di-columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);
  useEffect(() => {
    localStorage.setItem('di-column-widths', JSON.stringify(columnWidths));
  }, [columnWidths]);

  // Close column menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target as Node)) {
        setShowColumnMenu(false);
      }
    };
    if (showColumnMenu) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColumnMenu]);

  // ==================== History Fetching ====================

  const fetchHistory = useCallback(async (page?: number, perPage?: number, source?: SourceFilter) => {
    const p = page ?? paginationRef.current.page;
    const pp = perPage ?? paginationRef.current.per_page;
    const src = source ?? sourceFilterRef.current;

    try {
      const params = new URLSearchParams({
        page: String(p),
        per_page: String(pp),
        source: src,
      });
      const res = await fetch(`${API_BASE_URL}/api/web/data/import-history?${params}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setImports(data.imports);
        setPagination(data.pagination);
        setHasActiveJobs(data.has_active_jobs);
        setActiveJobIds(data.active_job_ids || []);

        // Auto-recover polling for active jobs (e.g., after page reload)
        if (data.active_job_ids?.length > 0) {
          for (const jid of data.active_job_ids) {
            if (!pollingTimersRef.current.has(jid)) {
              startPollingRef.current(jid);
            }
          }
        }
      }
    } catch {
      // Silently fail
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchHistory(1, 20, 'all');
  }, [fetchHistory]);

  // Auto-refresh when active jobs exist
  useEffect(() => {
    if (hasActiveJobs) {
      historyRefreshRef.current = setInterval(() => {
        fetchHistory();
      }, HISTORY_REFRESH_INTERVAL);
    } else if (historyRefreshRef.current) {
      clearInterval(historyRefreshRef.current);
      historyRefreshRef.current = null;
    }
    return () => {
      if (historyRefreshRef.current) clearInterval(historyRefreshRef.current);
    };
  }, [hasActiveJobs, fetchHistory]);

  // Cleanup all polling timers on unmount
  useEffect(() => {
    return () => {
      pollingTimersRef.current.forEach((timer) => clearInterval(timer));
      pollingTimersRef.current.clear();
    };
  }, []);

  // ==================== Upload Logic ====================

  const detectFileType = (file: File): 'fit' | 'zip' | 'csv' | null => {
    const name = file.name.toLowerCase();
    if (name.endsWith('.fit')) return 'fit';
    if (name.endsWith('.zip')) return 'zip';
    if (name.endsWith('.csv')) return 'csv';
    return null;
  };

  const validateFileSize = (file: File, type: 'fit' | 'zip' | 'csv'): string | null => {
    const maxSize = type === 'zip' ? MAX_ZIP_SIZE : type === 'fit' ? MAX_FIT_SIZE : MAX_CSV_SIZE;
    const maxLabel = type === 'zip' ? '500MB' : '50MB';
    if (file.size > maxSize) {
      return `File too large. Maximum size for ${type.toUpperCase()} files is ${maxLabel}.`;
    }
    return null;
  };

  const getEndpoint = (type: 'fit' | 'zip' | 'csv'): string => {
    switch (type) {
      case 'fit': return `${API_BASE_URL}/api/web/data/upload-fit`;
      case 'zip': return `${API_BASE_URL}/api/web/data/upload-bulk`;
      case 'csv': return `${API_BASE_URL}/api/web/data/upload-tp-export`;
    }
  };

  const startPolling = (jobId: string) => {
    // Don't start duplicate polling for same job
    if (pollingTimersRef.current.has(jobId)) return;

    const startTime = Date.now();
    const timer = setInterval(async () => {
      if (Date.now() - startTime > POLL_TIMEOUT) {
        clearInterval(timer);
        pollingTimersRef.current.delete(jobId);
        return;
      }
      try {
        const res = await fetch(`${API_BASE_URL}/api/web/data/processing-status/${jobId}`, {
          credentials: 'include',
        });
        if (res.ok) {
          const status: ProcessingStatus = await res.json();
          setJobStatuses((prev) => {
            const next = new Map(prev);
            next.set(jobId, status);
            return next;
          });
          if (status.is_done) {
            clearInterval(timer);
            pollingTimersRef.current.delete(jobId);
            fetchHistory();
          }
        }
      } catch {
        // Continue polling on error
      }
    }, POLL_INTERVAL);
    pollingTimersRef.current.set(jobId, timer);
  };
  startPollingRef.current = startPolling;

  const handleUpload = async (file: File) => {
    const fileType = detectFileType(file);
    if (!fileType) {
      setUploadState('error');
      setUploadError('Unsupported file type. Please upload .fit, .zip, or .csv files.');
      return;
    }

    const sizeError = validateFileSize(file, fileType);
    if (sizeError) {
      setUploadState('error');
      setUploadError(sizeError);
      return;
    }

    // Large ZIP files: upload directly to GCS via signed URL to bypass Cloud Run 32MB body limit
    if (fileType === 'zip' && file.size > GCS_UPLOAD_THRESHOLD) {
      await handleLargeZipUpload(file);
      return;
    }

    setUploadState('uploading');
    setUploadingFilename(file.name);
    setUploadResult(null);
    setUploadError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(getEndpoint(fileType), {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: 'Upload failed' }));
        setUploadState('error');
        setUploadError(errData.error || `Upload failed (${res.status})`);
        return;
      }

      const data = await res.json();
      setUploadState('success');

      if (fileType === 'fit') {
        setUploadResult({ type: 'fit', data: data as FitUploadResult });
      } else if (fileType === 'zip') {
        const bulkData = data as BulkUploadResult;
        setUploadResult({ type: 'bulk', data: bulkData });
        if (bulkData.job_id && bulkData.pending > 0) {
          startPolling(bulkData.job_id);
        }
      } else {
        setUploadResult({ type: 'csv', data: data as CsvUploadResult });
      }

      fetchHistory();
    } catch {
      setUploadState('error');
      setUploadError('Network error. Please check your connection and try again.');
    }
  };

  const handleLargeZipUpload = async (file: File) => {
    setUploadState('uploading');
    setUploadingFilename(file.name);
    setUploadResult(null);
    setUploadError('');

    try {
      // Step 1: Get signed upload URL from backend
      const urlRes = await fetch(`${API_BASE_URL}/api/web/data/upload-bulk-url`, {
        credentials: 'include',
      });
      if (!urlRes.ok) {
        const err = await urlRes.json().catch(() => ({ error: 'Failed to get upload URL' }));
        setUploadState('error');
        setUploadError(err.error || 'Failed to get upload URL');
        return;
      }
      const { upload_url, job_id } = await urlRes.json();

      // Step 2: Upload ZIP directly to GCS (no credentials — signed URL handles auth)
      const gcsRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/zip' },
        body: file,
      });
      if (!gcsRes.ok) {
        setUploadState('error');
        setUploadError(`Upload to storage failed (${gcsRes.status}). Please try again.`);
        return;
      }

      // Step 3: Tell backend to process the uploaded ZIP
      const processRes = await fetch(`${API_BASE_URL}/api/web/data/process-bulk-gcs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id }),
      });
      if (!processRes.ok) {
        const err = await processRes.json().catch(() => ({ error: 'Processing failed' }));
        setUploadState('error');
        setUploadError(err.error || `Processing failed (${processRes.status})`);
        return;
      }

      const data = await processRes.json() as BulkUploadResult;
      setUploadState('success');
      setUploadResult({ type: 'bulk', data });
      if (data.job_id && data.pending > 0) {
        startPolling(data.job_id);
      }
      fetchHistory();
    } catch {
      setUploadState('error');
      setUploadError('Network error. Please check your connection and try again.');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadState('dragging');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadState('idle');
  };

  const handleMultiFitUpload = async (files: File[]) => {
    if (files.length > 10) {
      setUploadState('error');
      setUploadError('Maximum 10 FIT files at once.');
      return;
    }
    setUploadState('uploading');
    setUploadingFilename(`${files.length} FIT files`);
    setUploadResult(null);
    setUploadError('');

    const formData = new FormData();
    files.forEach(f => formData.append('files', f));

    try {
      const res = await fetch(`${API_BASE_URL}/api/web/data/upload-multi-fit`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: 'Upload failed' }));
        setUploadState('error');
        setUploadError(errData.error || `Upload failed (${res.status})`);
        return;
      }
      const data = await res.json();
      setUploadState('success');
      setUploadResult({ type: 'bulk', data: { ...data, job_id: '', pending: 0, background_processing: false } });
      fetchHistory();
    } catch {
      setUploadState('error');
      setUploadError('Network error.');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    const fitFiles = files.filter(f => f.name.toLowerCase().endsWith('.fit'));
    if (fitFiles.length > 1) {
      handleMultiFitUpload(fitFiles);
    } else if (files.length > 0) {
      handleUpload(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 1) {
      const fitFiles = Array.from(files).filter(f => f.name.toLowerCase().endsWith('.fit'));
      if (fitFiles.length > 1) {
        handleMultiFitUpload(fitFiles);
      } else {
        handleUpload(files[0]);
      }
    } else if (files && files.length === 1) {
      handleUpload(files[0]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRetry = () => {
    setUploadState('idle');
    setUploadError('');
    setUploadResult(null);
    // Don't clear job statuses — they persist in the background progress section
  };

  // ==================== Delete Logic ====================

  const isRecordDeletable = (record: ImportRecord): boolean => {
    if (typeof record.id === 'string' && String(record.id).startsWith('strava_')) return false;
    return record.deletable !== false;
  };

  const handleDeleteSingle = async (id: number | string) => {
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/data/import/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setDeleteConfirm(null);
        setSelectedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
        fetchHistory();
      }
    } catch {
      // Silently fail
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/data/imports`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      if (res.ok) {
        setSelectedIds(new Set());
        fetchHistory();
      }
    } catch {
      // Silently fail
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (id: number | string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const deletableImports = imports.filter(i => isRecordDeletable(i));
    if (selectedIds.size === deletableImports.length && deletableImports.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(deletableImports.map(i => i.id)));
    }
  };

  // ==================== Column Menu ====================

  const toggleColumn = (colId: ColumnId) => {
    setVisibleColumns(prev => {
      if (prev.includes(colId)) {
        return prev.filter(c => c !== colId);
      }
      return [...prev, colId];
    });
  };

  const moveColumn = (colId: ColumnId, direction: number) => {
    setVisibleColumns(prev => {
      const idx = prev.indexOf(colId);
      if (idx < 0) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  // ==================== Pagination & Filters ====================

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.total_pages) return;
    setSelectedIds(new Set());
    fetchHistory(newPage);
  };

  const handlePerPageChange = (newPerPage: number) => {
    setSelectedIds(new Set());
    fetchHistory(1, newPerPage);
  };

  const handleSourceFilterChange = (newFilter: SourceFilter) => {
    setSourceFilter(newFilter);
    setSelectedIds(new Set());
    fetchHistory(1, pagination.per_page, newFilter);
  };

  // ==================== Formatters ====================

  const formatDuration = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    // Date-only string (e.g. "2026-03-06") — show date without time
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const d = new Date(dateStr + 'T00:00:00Z');
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
    }
    // If no timezone info, treat as local time (start_date_local from Strava)
    const d = dateStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr)
      ? new Date(dateStr)
      : new Date(dateStr + 'Z'); // Treat naive datetime as-is by using UTC then formatting in UTC
    const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
    if (!dateStr.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(dateStr)) {
      opts.timeZone = 'UTC'; // Display naive local time as-is
    }
    return d.toLocaleDateString('en-GB', opts);
  };

  const getSourceIcon = (sourceType: string): string => {
    switch (sourceType) {
      case 'single_fit': return 'FIT';
      case 'zip': return 'ZIP';
      case 'trainingpeaks_csv': return 'CSV';
      case 'strava_webhook': return 'STRAVA';
      case 'fit+strava': return 'FIT+S';
      case 'whoop': return 'WHOOP';
      case 'apple_health': case 'health_connect': return 'HEALTH';
      default: return sourceType?.toUpperCase()?.slice(0, 3) || '?';
    }
  };

  const getStatusClass = (status: string, sourceType?: string): string => {
    switch (status) {
      case 'completed': return 'di-status-completed';
      case 'duplicate': return 'di-status-duplicate';
      case 'processing': case 'pending': return 'di-status-processing';
      case 'failed': return 'di-status-failed';
      case 'synced': return sourceType === 'whoop' ? 'di-status-whoop' : (sourceType === 'apple_health' || sourceType === 'health_connect') ? 'di-status-health' : 'di-status-synced';
      default: return 'di-status-processing';
    }
  };

  // ==================== Cell Rendering ====================

  const renderCellContent = (col: ColumnId, record: ImportRecord) => {
    switch(col) {
      case 'name': {
        const displayName = record.source_type === 'fit+strava' && record.ride_info?.activity_name
          ? record.ride_info.activity_name
          : record.filename;
        return <span className="di-filename" title={displayName}>{displayName}</span>;
      }
      case 'filename': {
        const isFit = record.source_type === 'single_fit' || record.source_type === 'fit+strava' || record.source_type === 'zip';
        return isFit ? <span className="di-filename" title={record.filename}>{record.filename}</span> : '-';
      }
      case 'source': return <span className={`di-type-badge ${record.source_type === 'strava_webhook' ? 'di-type-strava' : record.source_type === 'whoop' ? 'di-type-whoop' : (record.source_type === 'apple_health' || record.source_type === 'health_connect') ? 'di-type-health' : ''}`}>{getSourceIcon(record.source_type)}</span>;
      case 'status': return <span className={`di-status-badge ${getStatusClass(record.status, record.source_type)}`}>{record.status}</span>;
      case 'date': return <span className="di-date">{formatDateTime(record.ride_info?.start_date_local || record.ride_info?.recorded_at || record.whoop_data?.date || record.created_at || null)}</span>;
      case 'duration': return record.ride_info?.duration_seconds ? formatDuration(record.ride_info.duration_seconds) : '-';
      case 'np': return record.ride_info?.np_watts ? `${Math.round(record.ride_info.np_watts)}W` : '-';
      case 'tss': return record.ride_info?.tss ? Math.round(record.ride_info.tss) : '-';
      case 'type': return record.ride_info?.activity_type || '-';
      case 'activity': return record.ride_info?.activity_name || (record.matched_activity_id ? `#${record.matched_activity_id}` : '-');
      case 'imported': return <span className="di-date">{formatDate(record.created_at)}</span>;
      case 'data_points': return record.ride_info?.data_points?.toLocaleString() || '-';
      case 'hr': return record.ride_info?.has_hr ? '\u2713' : '\u2717';
      case 'error': return record.error_message ? <span className="di-error-hint" title={record.error_message}>Error</span> : '-';
    }
  };

  // ==================== Job Actions ====================

  const [jobAction, setJobAction] = useState<'idle' | 'cancelling' | 'retrying'>('idle');

  const handleCancelJob = async (jobId: string) => {
    if (!confirm('Delete all pending imports for this job?')) return;
    setJobAction('cancelling');
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/data/job/${jobId}/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        // Stop polling for this job and remove its status
        const timer = pollingTimersRef.current.get(jobId);
        if (timer) {
          clearInterval(timer);
          pollingTimersRef.current.delete(jobId);
        }
        setJobStatuses((prev) => {
          const next = new Map(prev);
          next.delete(jobId);
          return next;
        });
        fetchHistory();
      }
    } catch {
      // Silently fail
    } finally {
      setJobAction('idle');
    }
  };

  const handleRetryJob = async (jobId: string) => {
    setJobAction('retrying');
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/data/job/${jobId}/retry`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        startPolling(jobId);
      } else {
        const data = await res.json().catch(() => ({ error: 'Retry failed' }));
        alert(data.error || 'Retry failed');
      }
    } catch {
      alert('Network error');
    } finally {
      setJobAction('idle');
    }
  };

  // ==================== Render ====================

  const renderDropZone = () => {
    const dropZoneClass = `di-drop-zone ${uploadState === 'dragging' ? 'di-dragging' : ''}`;
    return (
      <div
        className={dropZoneClass}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".fit,.FIT,.zip,.ZIP,.csv,.CSV"
          onChange={handleFileSelect}
          className="di-file-input"
          multiple
        />
        {uploadState === 'idle' || uploadState === 'dragging' ? (
          <div className="di-drop-content">
            <div className="di-drop-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <p className="di-drop-text">Drag files here or click to browse</p>
            <p className="di-drop-hint">.FIT · .ZIP (bulk) · .CSV (TrainingPeaks export)</p>
            <p className="di-drop-hint di-drop-hint-details">
              Accepted: .FIT activity files, or .ZIP/.GZ archives containing .FIT files only.<br />
              Media files (photos, videos) inside archives are not supported. Max file size: 250 MB.
            </p>
          </div>
        ) : uploadState === 'uploading' ? (
          <div className="di-upload-progress">
            <div className="di-spinner" />
            <p>Uploading {uploadingFilename}...</p>
          </div>
        ) : null}
      </div>
    );
  };

  const renderUploadResult = () => {
    if (uploadState === 'error') {
      return (
        <div className="di-result di-result-error">
          <p className="di-error-text">{uploadError}</p>
          <button className="di-retry-btn" onClick={handleRetry}>Try again</button>
        </div>
      );
    }

    if (uploadState !== 'success' || !uploadResult) return null;

    if (uploadResult.type === 'fit') {
      const { summary, matched_activity_id } = uploadResult.data;
      return (
        <div className="di-result di-result-success">
          <div className="di-result-header">
            <span className="di-check-icon">&#10003;</span>
            <span>Upload complete</span>
            <button className="di-close-result" onClick={handleRetry}>&times;</button>
          </div>
          <div className="di-summary-card">
            <div className="di-summary-row">
              <span>Data points</span>
              <span>{summary.data_points.toLocaleString()}</span>
            </div>
            <div className="di-summary-row">
              <span>Power data</span>
              <span>{summary.has_power ? '\u2713' : '\u2717'}</span>
            </div>
            <div className="di-summary-row">
              <span>Heart rate</span>
              <span>{summary.has_hr ? '\u2713' : '\u2717'}</span>
            </div>
            <div className="di-summary-row">
              <span>Duration</span>
              <span>{formatDuration(summary.duration_seconds)}</span>
            </div>
            <div className="di-summary-row">
              <span>Matched activity</span>
              <span>{matched_activity_id ? `#${matched_activity_id}` : 'No match'}</span>
            </div>
          </div>
        </div>
      );
    }

    if (uploadResult.type === 'bulk') {
      const { total_files, processed, failed, job_id } = uploadResult.data;
      const currentJobStatus = job_id ? jobStatuses.get(job_id) : null;
      return (
        <div className="di-result di-result-success">
          <div className="di-result-header">
            <span className="di-check-icon">&#10003;</span>
            <span>Bulk upload started: {processed} inline, rest processing in background</span>
            <button className="di-close-result" onClick={handleRetry}>&times;</button>
          </div>
          {currentJobStatus && !currentJobStatus.is_done && (
            <div className="di-polling-bar">
              <div className="di-progress-track">
                <div
                  className="di-progress-fill"
                  style={{ width: `${((currentJobStatus.completed + currentJobStatus.failed) / currentJobStatus.total) * 100}%` }}
                />
              </div>
              <p className="di-polling-text">
                Background: {currentJobStatus.completed} done, {currentJobStatus.pending} pending
                {currentJobStatus.failed > 0 && `, ${currentJobStatus.failed} failed`}
                {' '}of {currentJobStatus.total}
              </p>
            </div>
          )}
          {currentJobStatus && currentJobStatus.is_done && (
            <div className="di-polling-bar">
              <div className="di-progress-track">
                <div className="di-progress-fill" style={{ width: '100%' }} />
              </div>
              <p className="di-polling-text">
                Complete: {currentJobStatus.completed} processed
                {currentJobStatus.failed > 0 && `, ${currentJobStatus.failed} failed`}
                {' '}of {currentJobStatus.total}
              </p>
            </div>
          )}
          {!currentJobStatus && (
            <div className="di-summary-card">
              <div className="di-summary-row">
                <span>Total files</span><span>{total_files}</span>
              </div>
              <div className="di-summary-row">
                <span>Processed inline</span><span>{processed}</span>
              </div>
              <div className="di-summary-row">
                <span>Failed</span><span>{failed}</span>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (uploadResult.type === 'csv') {
      const { total_rows, processed, matched } = uploadResult.data;
      return (
        <div className="di-result di-result-success">
          <div className="di-result-header">
            <span className="di-check-icon">&#10003;</span>
            <span>TrainingPeaks import complete</span>
            <button className="di-close-result" onClick={handleRetry}>&times;</button>
          </div>
          <div className="di-summary-card">
            <div className="di-summary-row"><span>Total rows</span><span>{total_rows}</span></div>
            <div className="di-summary-row"><span>Processed</span><span>{processed}</span></div>
            <div className="di-summary-row"><span>Matched</span><span>{matched}</span></div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderBackgroundProgress = () => {
    // Collect jobs to show: active jobs from backend + any tracked jobs with polling status
    const currentUploadJobId = uploadResult?.type === 'bulk' ? uploadResult.data.job_id : null;

    // Jobs to show in standalone section (exclude current upload's job — it's shown in upload result)
    const jobsToShow = activeJobIds.filter((jid) => jid !== currentUploadJobId);

    // Also include any tracked jobs that aren't in activeJobIds but have status (just finished)
    jobStatuses.forEach((status, jid) => {
      if (!status.is_done && !jobsToShow.includes(jid) && jid !== currentUploadJobId) {
        jobsToShow.push(jid);
      }
    });

    if (jobsToShow.length === 0) return null;

    return (
      <>
        {jobsToShow.map((jobId) => {
          const status = jobStatuses.get(jobId);
          const isStuck = !status;

          return (
            <div key={jobId} className="di-background-progress">
              <div className="di-result-header">
                <span className="di-check-icon" style={{ color: '#9CA3AF' }}>&#9881;</span>
                <span>
                  {isStuck ? 'Stuck background job' : 'Background processing'}
                </span>
                <div className="di-job-actions">
                  <button
                    className="di-job-retry-btn"
                    onClick={() => handleRetryJob(jobId)}
                    disabled={jobAction !== 'idle'}
                    title="Re-enqueue background processing"
                  >
                    {jobAction === 'retrying' ? 'Retrying...' : 'Retry'}
                  </button>
                  <button
                    className="di-job-cancel-btn"
                    onClick={() => handleCancelJob(jobId)}
                    disabled={jobAction !== 'idle'}
                    title="Delete all pending imports for this job"
                  >
                    {jobAction === 'cancelling' ? 'Cancelling...' : 'Cancel'}
                  </button>
                </div>
              </div>
              {status && (
                <div className="di-polling-bar">
                  <div className="di-progress-track">
                    <div
                      className="di-progress-fill"
                      style={{ width: `${((status.completed + status.failed) / status.total) * 100}%` }}
                    />
                  </div>
                  <p className="di-polling-text">
                    {status.completed} done, {status.pending} pending
                    {status.processing > 0 && `, ${status.processing} processing`}
                    {status.failed > 0 && `, ${status.failed} failed`}
                    {' '}of {status.total}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  const renderColumnMenu = () => {
    if (!showColumnMenu) return null;
    return (
      <div className="di-column-menu">
        <div className="di-column-menu-header">
          <span>Columns</span>
          <button className="di-column-menu-close" onClick={() => setShowColumnMenu(false)}>&times;</button>
        </div>
        <div className="di-column-menu-list">
          {visibleColumns.map((colId, idx) => {
            const col = ALL_COLUMNS.find(c => c.id === colId)!;
            return (
              <div key={colId} className="di-column-menu-item">
                <input type="checkbox" checked onChange={() => toggleColumn(colId)} />
                <span>{col.label}</span>
                <div className="di-column-menu-arrows">
                  <button disabled={idx === 0} onClick={() => moveColumn(colId, -1)}>&uarr;</button>
                  <button disabled={idx === visibleColumns.length - 1} onClick={() => moveColumn(colId, 1)}>&darr;</button>
                </div>
              </div>
            );
          })}
          {ALL_COLUMNS.filter(c => !visibleColumns.includes(c.id)).map(col => (
            <div key={col.id} className="di-column-menu-item di-column-hidden">
              <input type="checkbox" checked={false} onChange={() => toggleColumn(col.id)} />
              <span>{col.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderFilterBar = () => {
    const filters: { label: string; value: SourceFilter }[] = [
      { label: 'All', value: 'all' },
      { label: 'Imports', value: 'import' as SourceFilter },
      { label: 'Strava', value: 'strava' as SourceFilter },
      { label: 'WHOOP', value: 'whoop' as SourceFilter },
      { label: 'Health', value: 'health' as SourceFilter },
    ];

    return (
      <div className="di-filter-bar">
        <div className="di-filter-tabs">
          {filters.map((f) => (
            <button
              key={f.value}
              className={`di-filter-tab ${sourceFilter === f.value ? 'di-filter-active' : ''}`}
              onClick={() => handleSourceFilterChange(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="di-filter-right">
          {selectedIds.size > 0 && (
            <button
              className="di-delete-selected-btn"
              onClick={handleDeleteSelected}
              disabled={deleting}
            >
              Delete {selectedIds.size} selected
            </button>
          )}
          {hasActiveJobs && (
            <span className="di-active-indicator">Processing...</span>
          )}
          <div className="di-column-menu-wrapper" ref={columnMenuRef}>
            <button
              className="di-column-menu-trigger"
              onClick={() => setShowColumnMenu(prev => !prev)}
              title="Configure columns"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              Columns
            </button>
            {renderColumnMenu()}
          </div>
          <select
            className="di-per-page-select"
            value={pagination.per_page}
            onChange={(e) => handlePerPageChange(Number(e.target.value))}
          >
            {PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n} per page</option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const renderHistory = () => {
    if (historyLoading) {
      return <div className="di-history-loading">Loading...</div>;
    }

    if (imports.length === 0) {
      return (
        <div className="di-history-empty">
          {sourceFilter !== 'all'
            ? `No ${sourceFilter === 'health' ? 'Health' : sourceFilter} records found.`
            : 'No imports yet. Upload a FIT file or TrainingPeaks export to get started.'}
        </div>
      );
    }

    const deletableImports = imports.filter(i => isRecordDeletable(i));
    const allDeletableSelected = deletableImports.length > 0 && selectedIds.size === deletableImports.length;

    return (
      <>
        <div className="di-table-wrapper">
          <table className="di-history-table">
            <colgroup>
              <col style={{ width: '44px' }} />
              {visibleColumns.map(colId => (
                <col key={colId} style={{ width: `${getColumnWidth(colId)}px` }} />
              ))}
              <col style={{ width: '60px' }} />
            </colgroup>
            <thead>
              <tr>
                <th className="di-col-check">
                  <input
                    type="checkbox"
                    checked={allDeletableSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
                {visibleColumns.map(colId => {
                  const col = ALL_COLUMNS.find(c => c.id === colId)!;
                  return (
                    <th key={colId} className="di-th-resizable">
                      {col.label}
                      <span
                        className="di-resize-handle"
                        onMouseDown={(e) => handleResizeStart(e, colId)}
                      />
                    </th>
                  );
                })}
                <th className="di-col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {imports.map((record) => {
                const deletable = isRecordDeletable(record);
                return (
                  <tr
                    key={record.id}
                    className={`di-row-clickable ${selectedIds.has(record.id) ? 'di-row-selected' : ''}`}
                    onClick={() => setDetailRecord(record)}
                  >
                    <td className="di-col-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(record.id)}
                        onChange={() => toggleSelect(record.id)}
                        disabled={!deletable}
                      />
                    </td>
                    {visibleColumns.map(colId => (
                      <td key={colId}>
                        {renderCellContent(colId, record)}
                      </td>
                    ))}
                    <td className="di-col-actions" onClick={(e) => e.stopPropagation()}>
                      {deletable ? (
                        deleteConfirm === record.id ? (
                          <span className="di-confirm-delete">
                            <button
                              className="di-confirm-yes"
                              onClick={() => handleDeleteSingle(record.id)}
                              disabled={deleting}
                            >
                              Yes
                            </button>
                            <button
                              className="di-confirm-no"
                              onClick={() => setDeleteConfirm(null)}
                            >
                              No
                            </button>
                          </span>
                        ) : (
                          <button
                            className="di-delete-btn"
                            onClick={() => setDeleteConfirm(record.id)}
                            title="Delete import"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                        )
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  const renderPagination = () => {
    if (pagination.total_pages <= 1) return null;

    const pages: number[] = [];
    const start = Math.max(1, pagination.page - 2);
    const end = Math.min(pagination.total_pages, pagination.page + 2);
    for (let i = start; i <= end; i++) pages.push(i);

    return (
      <div className="di-pagination">
        <span className="di-pagination-info">
          {pagination.total} total imports
        </span>
        <div className="di-pagination-controls">
          <button
            className="di-page-btn"
            disabled={pagination.page <= 1}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            &lsaquo;
          </button>
          {pages.map((p) => (
            <button
              key={p}
              className={`di-page-btn ${p === pagination.page ? 'di-page-active' : ''}`}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </button>
          ))}
          <button
            className="di-page-btn"
            disabled={pagination.page >= pagination.total_pages}
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            &rsaquo;
          </button>
        </div>
      </div>
    );
  };

  const formatMs = (ms: number | null): string => {
    if (!ms) return '-';
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const renderDetailPopup = () => {
    if (!detailRecord) return null;

    const isWhoop = detailRecord.source_type === 'whoop';
    const isStrava = detailRecord.source_type === 'strava_webhook';
    const isFit = ['single_fit', 'zip', 'trainingpeaks_csv', 'fit+strava'].includes(detailRecord.source_type);
    const isAppleHealth = detailRecord.source_type === 'apple_health' || detailRecord.source_type === 'health_connect';

    const sourceLabel = isAppleHealth ? (detailRecord.source_type === 'health_connect' ? 'Health Connect' : 'Apple Health') : isWhoop ? 'WHOOP' : isStrava ? 'Strava' : 'File Import';
    const wd = detailRecord.whoop_data;
    const ri = detailRecord.ride_info;
    const ah = detailRecord.health_data;

    return (
      <div className="di-popup-overlay" onClick={() => setDetailRecord(null)}>
        <div className="di-popup" onClick={(e) => e.stopPropagation()}>
          <div className="di-popup-header">
            <div className="di-popup-title">
              <span className={`di-type-badge ${isStrava ? 'di-type-strava' : isWhoop ? 'di-type-whoop' : isAppleHealth ? 'di-type-health' : ''}`}>
                {getSourceIcon(detailRecord.source_type)}
              </span>
              <span>{detailRecord.filename}</span>
            </div>
            <button className="di-popup-close" onClick={() => setDetailRecord(null)}>&times;</button>
          </div>

          <div className="di-popup-body">
            {/* Common info */}
            <div className="di-popup-section">
              <div className="di-popup-section-title">Source</div>
              <div className="di-popup-grid">
                <div className="di-popup-field">
                  <span className="di-popup-label">Type</span>
                  <span className="di-popup-value">{sourceLabel}</span>
                </div>
                <div className="di-popup-field">
                  <span className="di-popup-label">Date</span>
                  <span className="di-popup-value">
                    {formatDateTime(ri?.start_date_local || detailRecord.created_at)}
                  </span>
                </div>
                <div className="di-popup-field">
                  <span className="di-popup-label">Status</span>
                  <span className={`di-status-badge ${getStatusClass(detailRecord.status, detailRecord.source_type)}`}>{detailRecord.status}</span>
                </div>
              </div>
            </div>

            {/* WHOOP data */}
            {isWhoop && wd && (
              <>
                {wd.recovery_score !== null && (
                  <div className="di-popup-section">
                    <div className="di-popup-section-title">Recovery</div>
                    <div className="di-popup-grid">
                      <div className="di-popup-field">
                        <span className="di-popup-label">Recovery Score</span>
                        <span className="di-popup-value di-popup-value-lg">{wd.recovery_score?.toFixed(0)}%</span>
                      </div>
                      {wd.resting_heart_rate !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Resting HR</span>
                          <span className="di-popup-value">{wd.resting_heart_rate?.toFixed(0)} bpm</span>
                        </div>
                      )}
                      {wd.hrv_rmssd_milli !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">HRV (RMSSD)</span>
                          <span className="di-popup-value">{wd.hrv_rmssd_milli?.toFixed(1)} ms</span>
                        </div>
                      )}
                      {wd.spo2_percentage !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">SpO2</span>
                          <span className="di-popup-value">{wd.spo2_percentage?.toFixed(1)}%</span>
                        </div>
                      )}
                      {wd.skin_temp_celsius !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Skin Temp</span>
                          <span className="di-popup-value">{wd.skin_temp_celsius?.toFixed(1)}&deg;C</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {wd.sleep_score !== null && (
                  <div className="di-popup-section">
                    <div className="di-popup-section-title">Sleep</div>
                    <div className="di-popup-grid">
                      <div className="di-popup-field">
                        <span className="di-popup-label">Sleep Score</span>
                        <span className="di-popup-value di-popup-value-lg">{wd.sleep_score?.toFixed(0)}%</span>
                      </div>
                      {wd.sleep_efficiency !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Efficiency</span>
                          <span className="di-popup-value">{wd.sleep_efficiency?.toFixed(0)}%</span>
                        </div>
                      )}
                      {wd.sleep_consistency !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Consistency</span>
                          <span className="di-popup-value">{wd.sleep_consistency?.toFixed(0)}%</span>
                        </div>
                      )}
                      {wd.total_in_bed_ms !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Time in Bed</span>
                          <span className="di-popup-value">{formatMs(wd.total_in_bed_ms)}</span>
                        </div>
                      )}
                      {wd.total_rem_ms !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">REM</span>
                          <span className="di-popup-value">{formatMs(wd.total_rem_ms)}</span>
                        </div>
                      )}
                      {wd.total_sws_ms !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Deep (SWS)</span>
                          <span className="di-popup-value">{formatMs(wd.total_sws_ms)}</span>
                        </div>
                      )}
                      {wd.total_light_sleep_ms !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Light</span>
                          <span className="di-popup-value">{formatMs(wd.total_light_sleep_ms)}</span>
                        </div>
                      )}
                      {wd.total_awake_ms !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Awake</span>
                          <span className="di-popup-value">{formatMs(wd.total_awake_ms)}</span>
                        </div>
                      )}
                      {wd.sleep_cycle_count !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Cycles</span>
                          <span className="di-popup-value">{wd.sleep_cycle_count}</span>
                        </div>
                      )}
                      {wd.disturbance_count !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Disturbances</span>
                          <span className="di-popup-value">{wd.disturbance_count}</span>
                        </div>
                      )}
                      {wd.respiratory_rate !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Resp. Rate</span>
                          <span className="di-popup-value">{wd.respiratory_rate?.toFixed(1)} br/min</span>
                        </div>
                      )}
                      {wd.sleep_needed_ms !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Sleep Needed</span>
                          <span className="di-popup-value">{formatMs(wd.sleep_needed_ms)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {wd.strain_score !== null && (
                  <div className="di-popup-section">
                    <div className="di-popup-section-title">Strain</div>
                    <div className="di-popup-grid">
                      <div className="di-popup-field">
                        <span className="di-popup-label">Strain Score</span>
                        <span className="di-popup-value di-popup-value-lg">{wd.strain_score?.toFixed(1)}</span>
                      </div>
                      {wd.average_heart_rate !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Avg HR</span>
                          <span className="di-popup-value">{wd.average_heart_rate?.toFixed(0)} bpm</span>
                        </div>
                      )}
                      {wd.max_heart_rate !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Max HR</span>
                          <span className="di-popup-value">{wd.max_heart_rate?.toFixed(0)} bpm</span>
                        </div>
                      )}
                      {wd.kilojoules !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Energy</span>
                          <span className="di-popup-value">{wd.kilojoules?.toFixed(0)} kJ</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {wd.workouts_json && wd.workouts_json.length > 0 && (
                  <div className="di-popup-section">
                    <div className="di-popup-section-title">Workouts ({wd.workouts_json.length})</div>
                    {wd.workouts_json.map((w: any, i: number) => (
                      <div key={i} className="di-popup-workout">
                        <span className="di-popup-workout-name">{w.sport_name || `Workout ${i + 1}`}</span>
                        {w.strain != null && <span>Strain: {w.strain.toFixed(1)}</span>}
                        {w.avg_hr != null && <span>Avg HR: {w.avg_hr.toFixed(0)}</span>}
                        {w.kilojoules != null && <span>{w.kilojoules.toFixed(0)} kJ</span>}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Health data (Apple Health / Health Connect) */}
            {isAppleHealth && ah && (
              <>
                {(ah.sleep_hours !== null || ah.in_bed_hours !== null) && (
                  <div className="di-popup-section">
                    <div className="di-popup-section-title">Sleep</div>
                    <div className="di-popup-grid">
                      {ah.sleep_hours !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Total Sleep</span>
                          <span className="di-popup-value di-popup-value-lg">{ah.sleep_hours.toFixed(1)}h</span>
                        </div>
                      )}
                      {ah.in_bed_hours !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">In Bed</span>
                          <span className="di-popup-value">{ah.in_bed_hours.toFixed(1)}h</span>
                        </div>
                      )}
                      {ah.deep_sleep_minutes !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Deep Sleep</span>
                          <span className="di-popup-value">{ah.deep_sleep_minutes}m</span>
                        </div>
                      )}
                      {ah.rem_sleep_minutes !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">REM</span>
                          <span className="di-popup-value">{ah.rem_sleep_minutes}m</span>
                        </div>
                      )}
                      {ah.core_sleep_minutes !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Core Sleep</span>
                          <span className="di-popup-value">{ah.core_sleep_minutes}m</span>
                        </div>
                      )}
                      {ah.awake_minutes !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Awake</span>
                          <span className="di-popup-value">{ah.awake_minutes}m</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(ah.resting_heart_rate !== null || ah.hrv_sdnn_ms !== null || ah.spo2_percentage !== null || ah.respiratory_rate !== null) && (
                  <div className="di-popup-section">
                    <div className="di-popup-section-title">Vitals</div>
                    <div className="di-popup-grid">
                      {ah.resting_heart_rate !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Resting HR</span>
                          <span className="di-popup-value">{ah.resting_heart_rate} bpm</span>
                        </div>
                      )}
                      {ah.hrv_sdnn_ms !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">HRV (SDNN)</span>
                          <span className="di-popup-value">{ah.hrv_sdnn_ms.toFixed(1)} ms</span>
                        </div>
                      )}
                      {ah.spo2_percentage !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">SpO2</span>
                          <span className="di-popup-value">{ah.spo2_percentage.toFixed(1)}%</span>
                        </div>
                      )}
                      {ah.respiratory_rate !== null && (
                        <div className="di-popup-field">
                          <span className="di-popup-label">Resp. Rate</span>
                          <span className="di-popup-value">{ah.respiratory_rate.toFixed(1)} br/min</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Ride data (FIT / Strava) */}
            {(isFit || isStrava) && ri && (
              <div className="di-popup-section">
                <div className="di-popup-section-title">Ride Data</div>
                <div className="di-popup-grid">
                  {ri.activity_name && (
                    <div className="di-popup-field">
                      <span className="di-popup-label">Activity</span>
                      <span className="di-popup-value">{ri.activity_name}</span>
                    </div>
                  )}
                  {ri.activity_type && (
                    <div className="di-popup-field">
                      <span className="di-popup-label">Type</span>
                      <span className="di-popup-value">{ri.activity_type}</span>
                    </div>
                  )}
                  {ri.duration_seconds != null && (
                    <div className="di-popup-field">
                      <span className="di-popup-label">Duration</span>
                      <span className="di-popup-value">{formatDuration(ri.duration_seconds)}</span>
                    </div>
                  )}
                  {ri.np_watts != null && (
                    <div className="di-popup-field">
                      <span className="di-popup-label">Normalized Power</span>
                      <span className="di-popup-value">{Math.round(ri.np_watts)}W</span>
                    </div>
                  )}
                  {ri.tss != null && (
                    <div className="di-popup-field">
                      <span className="di-popup-label">TSS</span>
                      <span className="di-popup-value">{Math.round(ri.tss)}</span>
                    </div>
                  )}
                  <div className="di-popup-field">
                    <span className="di-popup-label">Power Data</span>
                    <span className="di-popup-value">{ri.has_power ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="di-popup-field">
                    <span className="di-popup-label">Heart Rate</span>
                    <span className="di-popup-value">{ri.has_hr ? 'Yes' : 'No'}</span>
                  </div>
                  {ri.data_points != null && (
                    <div className="di-popup-field">
                      <span className="di-popup-label">Data Points</span>
                      <span className="di-popup-value">{ri.data_points.toLocaleString()}</span>
                    </div>
                  )}
                  {detailRecord.matched_activity_id && (
                    <div className="di-popup-field">
                      <span className="di-popup-label">Activity ID</span>
                      <span className="di-popup-value">#{detailRecord.matched_activity_id}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {detailRecord.error_message && (
              <div className="di-popup-section">
                <div className="di-popup-section-title" style={{ color: 'var(--ae-danger)' }}>Error</div>
                <p style={{ fontSize: '13px', color: 'var(--ae-danger)', margin: 0 }}>{detailRecord.error_message}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="di-container">
      <div className="di-upload-section">
        <h3 className="di-section-title">Import Data</h3>
        {renderDropZone()}
        {renderUploadResult()}
      </div>
      <div className="di-history-section">
        <h3 className="di-section-title">Import Manager</h3>
        {renderBackgroundProgress()}
        {renderFilterBar()}
        {renderHistory()}
      </div>
      {renderDetailPopup()}
    </div>
  );
};

export default DataImport;
